import { DataCollectionLevel } from 'containers/Esrs/pieces/DataCollection';
import {
  MappedDisclosureRequirements,
  Materiality,
  MaterialityState,
  MetricMaterialityState,
  ParentMaterialMetrics,
} from '../DoubleMaterialityAssessment.d';
import {
  Esrs_MaterialMetricTag_Constraint_,
  Esrs_MaterialMetricTag_Update_Column_,
  GetDrMaterialMetricsDocument_,
  GetEsrsStandardDocument_,
  GetMaterialStandardDocument_,
  GetMfDisclosureRequirementsMetricsQuery_,
  GetMFdrForMFstandardQuery_,
  GetParentAssessmentMaterialMetricsQuery_,
  GetRequiredTagsMaterialMetricsDocument_,
  GetStandardNonMandatoryMetricsDocument_,
  GetSubsidiaryRequiredTagsMaterialMetricsDocument_,
  QuestionType_Enum_,
  useUpdateSubsidiariesMaterialityMutation,
  useUpsertDrMaterialMetricsMutation,
} from 'models';
import { getMetricRefNumber } from 'containers/Esrs/pieces/DisclosureRequirements/Metrics/Metrics.hooks';
import { useCallback } from 'react';
import { MaterialMetric, Metric } from '../../MaterialityAssessment.d';
import { mapMaterialTags } from '../DoubleMaterialityAssessment.hooks';
import { FrameworkRefs } from 'containers/Esrs/pieces/Assessment';
import { useToast } from 'utils/hooks';

export const isParentMaterial = (
  ref: string,
  parentMetrics: ParentMaterialMetrics,
  parentMateriality?: MaterialityState | Materiality
) => {
  if (parentMateriality === MaterialityState.notMaterial) return false;
  return parentMetrics?.some((metric) => {
    return metric.metricRef === ref && metric.isMaterial === true;
  });
};

type PartialMetricsData = Omit<Metric, 'childrenMetrics'>;
interface MaybeHasChild extends PartialMetricsData {
  childrenMetrics?: { childMetric?: MaybeHasChild | null }[];
}

export type MfDisclosureRequirementTableMetric = {
  title: string;
  type: QuestionType_Enum_ | null | undefined;
  framework: FrameworkRefs;
  reference: string;
  subCategory: string;
  metric: Metric;
  description?: string | null;
  subCategoryReference: string;
};

export const isParentMaterialForSub = (
  ref: string,
  parentMetrics: ParentMaterialMetrics,
  parentMateriality?: MaterialityState | Materiality
) => {
  if (parentMateriality === MaterialityState.notMaterial) return false;
  return parentMetrics
    ?.filter((m) => m.metric.childrenMetricsCount === 0)
    .some((metric) => {
      return (
        metric.metricRef === ref &&
        metric.isMaterial === true &&
        metric.dataCollection === DataCollectionLevel.subsidiaries
      );
    });
};

const hasChildOnSubLevel = <T extends { metricRef: string; dataCollection?: string | null }>(
  metric: MaybeHasChild,
  parentMetrics?: T[]
): boolean => {
  const groupMetric = parentMetrics?.find((m) => m.metricRef === metric.reference);
  if (!!metric?.childrenMetrics?.length) {
    return metric.childrenMetrics?.some(
      (child) => child?.childMetric && hasChildOnSubLevel(child?.childMetric, parentMetrics)
    );
  }
  return groupMetric?.dataCollection === DataCollectionLevel.subsidiaries;
};

const determineMateriality = (
  materialMetric: any,
  groupMetric: any,
  metric: any,
  isCollectOnly: boolean,
  shouldCollectDataMandatory: boolean,
  assessmentFrameworkRef?: string,
  parentMetrics?: any[]
): MetricMaterialityState => {
  if (isCollectOnly) {
    if (metric.childrenMetrics?.length) {
      if (
        hasChildOnSubLevel(metric as MaybeHasChild, parentMetrics) &&
        shouldCollectDataMandatory
      ) {
        return MetricMaterialityState.collectDataMandatory;
      } else {
        return materialMetric?.isMaterial
          ? MetricMaterialityState.collectData
          : MetricMaterialityState.doNotCollect;
      }
    } else if (
      groupMetric?.isMaterial &&
      groupMetric.dataCollection === DataCollectionLevel.subsidiaries &&
      shouldCollectDataMandatory
    ) {
      return MetricMaterialityState.collectDataMandatory;
    } else {
      return materialMetric?.isMaterial
        ? MetricMaterialityState.collectData
        : MetricMaterialityState.doNotCollect;
    }
  } else if (materialMetric?.isPhasedIn) {
    return MetricMaterialityState.phasedIn;
  } else if (materialMetric?.isDataGatheringOnly) {
    return MetricMaterialityState.gatherData;
  } else if (materialMetric?.isMaterial === true) {
    return MetricMaterialityState.material;
  } else if (materialMetric?.isMaterial === false) {
    return MetricMaterialityState.notMaterial;
  } else if (metric?.requirement?.group?.standard?.frameworkRef !== assessmentFrameworkRef) {
    return MetricMaterialityState.doNotInclude;
  }

  return MetricMaterialityState.toAssess;
};

export const handleMetric = (
  metric: Metric,
  materialMetrics: MaterialMetric[],
  isDRAssessed: boolean,
  isCollectOnly: boolean,
  parentMetrics?: ParentMaterialMetrics,
  drParentMateriality?: MaterialityState | Materiality,
  assessmentFrameworkRef?: string
) => {
  const additionalTypes =
    metric.additionalTypes.map((addType) => {
      const type = addType.additionalType;
      return {
        reference: type.reference,
        title: type.title ?? '',
        description: type.description ?? '',
        helpText: type.learnMore ?? '',
      };
    }) ?? [];
  const voluntaryTagIndex = additionalTypes.findIndex((e) => e.title === 'Voluntary');
  if (voluntaryTagIndex !== -1) {
    additionalTypes.unshift(additionalTypes.splice(voluntaryTagIndex, 1)[0]);
  }
  let materiality = MetricMaterialityState.toAssess;
  const materialMetric = materialMetrics?.find((m) => m.metricRef === metric.reference);
  const groupMetric = parentMetrics?.find((m) => m.metricRef === metric.reference);

  if ((materialMetric || (groupMetric && metric.childrenMetrics.length)) && isDRAssessed) {
    const shouldCollectDataMandatory =
      (groupMetric?.isMaterial &&
        (drParentMateriality === MaterialityState.material ||
          drParentMateriality === MaterialityState.mandatory ||
          drParentMateriality === MaterialityState.gatherData ||
          drParentMateriality === MaterialityState.phasedIn)) ??
      false;
    materiality = determineMateriality(
      materialMetric,
      groupMetric,
      metric,
      isCollectOnly,
      shouldCollectDataMandatory,
      assessmentFrameworkRef,
      parentMetrics
    );
  } else if (metric.requirement?.group?.standard?.frameworkRef !== assessmentFrameworkRef) {
    materiality = MetricMaterialityState.doNotInclude;
  }
  const metricRefNumber = getMetricRefNumber(metric);

  return {
    materiality,
    additionalTypes,
    metricRefNumber,
  };
};

export const getMetricMaterialityForStandardBrowser = (
  metric: GetMFdrForMFstandardQuery_['MFStandard'][0]['mfDisclosureRequirementGroups'][0]['mfDisclosureRequirements'][0]['mfDisclosureRequirementItems'][0]['metric'],
  isCollectOnly: boolean,
  assessmentFrameworkRef: string,
  parentMetrics?: GetParentAssessmentMaterialMetricsQuery_['esrs_MaterialMetric'] | undefined
) => {
  let materiality = MetricMaterialityState.toAssess;
  const groupMetric = parentMetrics?.find((m) => m.metricRef === metric.reference);
  const isParentStandardMaterial = groupMetric?.materialStandard.isMaterial;
  const materialMetric = metric.materialMetrics?.[0];

  if (materialMetric || (groupMetric && metric.childrenMetrics.length)) {
    const shouldCollectDataMandatory =
      (groupMetric?.isMaterial && isParentStandardMaterial) ?? false;

    materiality = determineMateriality(
      materialMetric,
      groupMetric,
      metric,
      isCollectOnly,
      shouldCollectDataMandatory,
      assessmentFrameworkRef,
      parentMetrics
    );
  } else if (assessmentFrameworkRef !== metric?.requirement?.frameworkRef) {
    materiality = MetricMaterialityState.doNotInclude;
  }

  return {
    materiality,
  };
};

export const updateAllMateriality = (
  metrics: MappedDisclosureRequirements[number]['metrics'],
  materiality: MaterialityState | null,
  drMaterialMetrics: MaterialMetric[],
  drRef: string
) => {
  const isMaterial =
    materiality === MaterialityState.material ||
    materiality === MaterialityState.gatherData ||
    materiality === MaterialityState.phasedIn ||
    materiality === MaterialityState.collectData
      ? true
      : materiality === MaterialityState.notMaterial ||
          materiality === MaterialityState.doNotCollect
        ? false
        : null;
  const isEntitySpecific = (m: MappedDisclosureRequirements[number]['metrics'][number]) => {
    return m.additionalTypes.some((type) => type.additionalType.reference === 'Entity-specific');
  };

  // Check if the parent metric is entity-specific
  const isParentMetricEntitySpecific = (
    m: MappedDisclosureRequirements[number]['metrics'][number]
  ) => {
    const parentMetricsRefs = m.parentMetrics.map((pm) => pm.parentMetric.reference);
    const parentMetrics = metrics.filter((m) => parentMetricsRefs.includes(m.reference));
    return parentMetrics.some((pm) => isEntitySpecific(pm));
  };

  const hasPhasedIn = (m: MappedDisclosureRequirements[number]['metrics'][number]) => {
    return m.additionalTypes.some((type) => type.additionalType.title?.includes('Phased'));
  };

  // MF material metrics
  const mfMaterialMetrics = drMaterialMetrics.filter(
    (mm) => mm.metric?.requirement?.reference !== drRef
  );

  const updatedDrMaterialMetrics = metrics.map((m) => ({
    metricRef: m.reference,
    isMaterial:
      isEntitySpecific(m) || (isParentMetricEntitySpecific(m) && !m.isAssessable)
        ? false
        : isMaterial,
    isDataGatheringOnly:
      isEntitySpecific(m) || (isParentMetricEntitySpecific(m) && !m.isAssessable)
        ? false
        : materiality === MaterialityState.gatherData || materiality === MaterialityState.phasedIn,
    metric: m,
    dataCollection: drMaterialMetrics.find((mm) => mm.metricRef === m.reference)?.dataCollection,
    isPhasedIn:
      isEntitySpecific(m) || (isParentMetricEntitySpecific(m) && !m.isAssessable)
        ? false
        : hasPhasedIn(m) && materiality === MaterialityState.phasedIn,
  }));

  return [...updatedDrMaterialMetrics, ...mfMaterialMetrics];
};

export const checkMateriality = (value: MetricMaterialityState, isCollectOnly: boolean) => {
  if (isCollectOnly) {
    if (value === MetricMaterialityState.collectData) {
      return true;
    } else if (value === MetricMaterialityState.doNotCollect) {
      return false;
    }
  } else {
    if (
      value === MetricMaterialityState.material ||
      value === MetricMaterialityState.gatherData ||
      value === MetricMaterialityState.phasedIn
    ) {
      return true;
    } else if (value === MetricMaterialityState.notMaterial) {
      return false;
    } else {
      return null;
    }
  }
};
const shouldDataCollectionLevelBeGroup = (drRef?: string) => {
  const relevantDrs = ['E1-8', 'E1-9', 'S1-16'];
  return relevantDrs.includes(drRef ?? '');
};

export const useSaveDrMetrics = () => {
  const [upsertMaterialMetrics] = useUpsertDrMaterialMetricsMutation();
  const [updateSubsidiariesMateriality] = useUpdateSubsidiariesMaterialityMutation();
  const toast = useToast();

  return useCallback(
    (metrics: MaterialMetric[], isGroup: boolean, materialStandardId: string, drRef?: string) => {
      const defaultDataCollection = isGroup
        ? DataCollectionLevel.subsidiaries
        : DataCollectionLevel.company;
      return upsertMaterialMetrics({
        variables: {
          objects: metrics.map((m) => ({
            metricRef: m.metricRef,
            isMaterial: m.isMaterial,
            isDataGatheringOnly: m.isDataGatheringOnly,
            isPhasedIn: m.isPhasedIn,
            materialStandardId,
            materialMetricTags: {
              data: mapMaterialTags(m.metric),
              on_conflict: {
                constraint:
                  Esrs_MaterialMetricTag_Constraint_.MaterialMetricTagMaterialMetricIdTagTypeKey_,
                update_columns: [Esrs_MaterialMetricTag_Update_Column_.TagType_],
              },
            },
            dataCollection: isGroup
              ? shouldDataCollectionLevelBeGroup(drRef)
                ? DataCollectionLevel.group
                : DataCollectionLevel.subsidiaries
              : DataCollectionLevel.company,
          })),
        },
        refetchQueries: [
          GetDrMaterialMetricsDocument_,
          GetEsrsStandardDocument_,
          GetMaterialStandardDocument_,
          GetRequiredTagsMaterialMetricsDocument_,
          GetStandardNonMandatoryMetricsDocument_,
          GetSubsidiaryRequiredTagsMaterialMetricsDocument_,
        ],
      })
        .then(() => {
          const hasMaterialMetrics = metrics.some((mm) => mm.isMaterial !== false);
          const hasSubsidiaryLevel = metrics.some(
            (mm) =>
              (mm.dataCollection ?? defaultDataCollection) === DataCollectionLevel.subsidiaries
          );
          if (isGroup && hasMaterialMetrics && hasSubsidiaryLevel) {
            updateSubsidiariesMateriality({
              variables: { id: materialStandardId },
            });
          }
          toast({
            text: 'Materiality updated!',
          });
        })
        .catch(() => {
          toast({ text: 'Failed to update materiality', variant: 'danger' });
        });
    },
    [upsertMaterialMetrics]
  );
};

export const isParentMetricOnGroupLevel = (
  allMetrics: Metric[],
  groupMetrics: ParentMaterialMetrics,
  metric: Metric
): boolean => {
  if (!metric.childrenMetrics?.length) {
    const groupMetric = groupMetrics?.find((m) => m.metricRef === metric.reference && m.isMaterial);

    return groupMetric?.dataCollection === DataCollectionLevel.group;
  }
  const childrenMetrics = allMetrics.find(
    (met) => met.reference === metric.reference
  )?.childrenMetrics;
  return (
    childrenMetrics?.every(
      (child) =>
        child.childMetric &&
        isParentMetricOnGroupLevel(allMetrics, groupMetrics, child.childMetric as Metric)
    ) ?? false
  );
};

export const processMfMetricsData = (
  data?: GetMfDisclosureRequirementsMetricsQuery_
): MfDisclosureRequirementTableMetric[] => {
  return (
    data?.MFDisclosureRequirementItem.map((item) => {
      const framework = item.metric.requirement.group.standard.frameworkRef as FrameworkRefs;
      return {
        title: item.metric.shortTitle ?? item.metric.title,
        type: item.metric.metricType,
        metric: item.metric,
        framework: framework,
        reference: item.metricRef,
        subCategory: item.mfDisclosureRequirement.title,
        description: item.metric.description,
        subCategoryReference: item.mfDisclosureRequirementRef,
      };
    }) ?? []
  );
};
